// src/utils/approvalUtils.js

/**
 * User Role Definitions
 */
export const USER_ROLES = {
  USER: 'user',           // Regular user - can conduct checks
  SUPER_USER: 'super-user', // Can set schedules and approve checks, but not create forms
  ADMIN: 'admin'          // Full access - can create forms, schedules, and approve
};

/**
 * Approval Decision Types
 */
export const APPROVAL_DECISIONS = {
  APPROVE: 'approve',
  REJECT: 'reject',
  REQUEST_CHANGES: 'request-changes'
};

/**
 * Approval Status for Submissions
 */
export const APPROVAL_STATUS = {
  NOT_REQUIRED: 'not-required',     // No approval workflow configured
  PENDING: 'pending',               // Awaiting first approval
  IN_REVIEW: 'in-review',           // At least one stage approved, more remain
  APPROVED: 'approved',             // All stages approved
  REJECTED: 'rejected',             // Rejected by an approver
  CHANGES_REQUESTED: 'changes-requested' // Changes requested, awaiting resubmission
};

/**
 * Approval Stage Requirement Types
 */
export const APPROVAL_REQUIREMENTS = {
  SIGNATURE: 'signature',
  COMMENT: 'comment',
  DECISION: 'decision'
};

/**
 * Approval Stage Data Structure
 * {
 *   stageNumber: 1,
 *   stageName: "Supervisor Review",
 *   approvers: {
 *     users: ["userId1", "userId2"],
 *     groups: ["groupId1"],
 *     roles: ["Health & Safety Lead"]
 *   },
 *   requirements: {
 *     signature: true,
 *     comment: false,
 *     decision: true
 *   },
 *   requireAll: false // If true, all approvers must approve; if false, any one can approve
 * }
 */

/**
 * Submission Approval Data Structure
 * /submissions/{submissionId}/approvals/{approvalId}
 * {
 *   stageNumber: 1,
 *   stageName: "Supervisor Review",
 *   status: "pending" | "approved" | "rejected" | "changes-requested",
 *   assignedTo: {
 *     users: ["userId1"],
 *     groups: ["groupId1"],
 *     roles: ["roleName"]
 *   },
 *   approvedBy: [{
 *     userId: "uid",
 *     userName: "John Doe",
 *     decision: "approve",
 *     signature: "dataURL",
 *     comment: "Looks good",
 *     timestamp: Timestamp
 *   }],
 *   createdAt: Timestamp,
 *   updatedAt: Timestamp
 * }
 */

/**
 * Check if user has permission based on role
 */
export function hasPermission(userRole, requiredRole) {
  const roleHierarchy = {
    [USER_ROLES.USER]: 1,
    [USER_ROLES.SUPER_USER]: 2,
    [USER_ROLES.ADMIN]: 3
  };
  
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

/**
 * Check if user can approve a specific stage
 */
export function canUserApproveStage(stage, userId, userGroups = [], userRoles = []) {
  if (!stage || !stage.approvers) return false;
  
  // Check if user is directly assigned
  if (stage.approvers.users && stage.approvers.users.includes(userId)) {
    return true;
  }
  
  // Check if user is in an assigned group
  if (stage.approvers.groups && userGroups.some(g => stage.approvers.groups.includes(g))) {
    return true;
  }
  
  // Check if user has an assigned role
  if (stage.approvers.roles && userRoles.some(r => stage.approvers.roles.includes(r))) {
    return true;
  }
  
  return false;
}

/**
 * Get all pending approvals for a user
 */
export function getPendingApprovalsForUser(submissions, userId, userGroups = [], userRoles = []) {
  const pending = [];
  
  submissions.forEach(submission => {
    if (!submission.approvals || submission.approvalStatus === APPROVAL_STATUS.APPROVED) {
      return;
    }
    
    // Check each approval stage
    submission.approvals.forEach(approval => {
      if (approval.status === 'pending' && canUserApproveStage(approval, userId, userGroups, userRoles)) {
        // Check if user hasn't already approved (in case requireAll is true)
        const alreadyApproved = approval.approvedBy && 
          approval.approvedBy.some(a => a.userId === userId);
        
        if (!alreadyApproved) {
          pending.push({
            submissionId: submission.id,
            submissionTitle: submission.formTitle,
            conductedBy: submission.conductedBy,
            conductedByName: submission.conductedByName,
            completedAt: submission.completedAt,
            approvalId: approval.id,
            stageNumber: approval.stageNumber,
            stageName: approval.stageName,
            requirements: approval.requirements || {},
            documentNumber: submission.documentNumber
          });
        }
      }
    });
  });
  
  return pending.sort((a, b) => {
    // Sort by completion date (oldest first)
    return a.completedAt?.toMillis() - b.completedAt?.toMillis();
  });
}

/**
 * Calculate overall approval status for a submission
 */
export function calculateApprovalStatus(approvals) {
  if (!approvals || approvals.length === 0) {
    return APPROVAL_STATUS.NOT_REQUIRED;
  }
  
  // Check if any stage is rejected
  if (approvals.some(a => a.status === 'rejected')) {
    return APPROVAL_STATUS.REJECTED;
  }
  
  // Check if any stage has changes requested
  if (approvals.some(a => a.status === 'changes-requested')) {
    return APPROVAL_STATUS.CHANGES_REQUESTED;
  }
  
  // Check if all stages are approved
  if (approvals.every(a => a.status === 'approved')) {
    return APPROVAL_STATUS.APPROVED;
  }
  
  // Check if at least one stage is approved (in review)
  if (approvals.some(a => a.status === 'approved')) {
    return APPROVAL_STATUS.IN_REVIEW;
  }
  
  // Otherwise, pending first approval
  return APPROVAL_STATUS.PENDING;
}

/**
 * Check if a stage is complete (all required approvals received)
 */
export function isStageComplete(stage) {
  if (!stage || stage.status !== 'pending') {
    return stage?.status === 'approved';
  }
  
  if (stage.requireAll) {
    // All assigned approvers must approve
    const totalApprovers = 
      (stage.assignedTo?.users?.length || 0) + 
      (stage.assignedTo?.groups?.length || 0) + 
      (stage.assignedTo?.roles?.length || 0);
    
    const approvedCount = stage.approvedBy?.length || 0;
    return approvedCount >= totalApprovers;
  } else {
    // Any one approver is sufficient
    return (stage.approvedBy?.length || 0) > 0;
  }
}

/**
 * Get the next pending approval stage
 */
export function getNextPendingStage(approvals) {
  if (!approvals || approvals.length === 0) return null;
  
  // Find first pending stage in order
  return approvals
    .sort((a, b) => a.stageNumber - b.stageNumber)
    .find(a => a.status === 'pending');
}

/**
 * Create approval requests from workflow configuration
 */
export function createApprovalRequests(workflowConfig) {
  if (!workflowConfig || !workflowConfig.stages || workflowConfig.stages.length === 0) {
    return [];
  }
  
  return workflowConfig.stages.map(stage => ({
    stageNumber: stage.stageNumber,
    stageName: stage.stageName,
    status: 'pending',
    approvers: {
      users: stage.approvers?.users || [],
      groups: stage.approvers?.groups || [],
      roles: stage.approvers?.roles || []
    },
    requirements: {
      signature: stage.requirements?.signature || false,
      comment: stage.requirements?.comment || false,
      decision: stage.requirements?.decision || true // Decision always required by default
    },
    requireAll: stage.requireAll || false,
    approvedBy: [],
    createdAt: new Date(),
    updatedAt: new Date()
  }));
}

/**
 * Validate approval action (check all requirements are met)
 */
export function validateApprovalAction(stage, approvalData) {
  const errors = [];
  
  if (!approvalData.decision) {
    errors.push('Decision is required');
  }
  
  if (stage.requirements?.signature && !approvalData.signature) {
    errors.push('Signature is required for this stage');
  }
  
  if (stage.requirements?.comment && (!approvalData.comment || approvalData.comment.trim() === '')) {
    errors.push('Comment is required for this stage');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Format approval status for display
 */
export function formatApprovalStatus(status) {
  const statusConfig = {
    [APPROVAL_STATUS.NOT_REQUIRED]: {
      label: 'No Approval Required',
      color: '#6b7280',
      icon: '✓'
    },
    [APPROVAL_STATUS.PENDING]: {
      label: 'Pending Approval',
      color: '#f59e0b',
      icon: '⏳'
    },
    [APPROVAL_STATUS.IN_REVIEW]: {
      label: 'In Review',
      color: '#3b82f6',
      icon: '👁️'
    },
    [APPROVAL_STATUS.APPROVED]: {
      label: 'Approved',
      color: '#10b981',
      icon: '✅'
    },
    [APPROVAL_STATUS.REJECTED]: {
      label: 'Rejected',
      color: '#ef4444',
      icon: '❌'
    },
    [APPROVAL_STATUS.CHANGES_REQUESTED]: {
      label: 'Changes Requested',
      color: '#f97316',
      icon: '🔄'
    }
  };
  
  return statusConfig[status] || statusConfig[APPROVAL_STATUS.NOT_REQUIRED];
}

/**
 * Get approval progress (e.g., "2/3 stages complete")
 */
export function getApprovalProgress(approvals) {
  if (!approvals || approvals.length === 0) {
    return { completed: 0, total: 0, percentage: 100 };
  }
  
  const completed = approvals.filter(a => a.status === 'approved').length;
  const total = approvals.length;
  const percentage = Math.round((completed / total) * 100);
  
  return { completed, total, percentage };
}

/**
 * Check if user can resubmit a rejected/changes-requested submission
 */
export function canResubmit(submission, userId) {
  if (!submission) return false;
  
  const status = submission.approvalStatus;
  const isOriginalConductor = submission.conductedBy === userId;
  
  return isOriginalConductor && 
    (status === APPROVAL_STATUS.REJECTED || status === APPROVAL_STATUS.CHANGES_REQUESTED);
}

/**
 * Get approval history summary for display
 */
export function getApprovalHistory(approvals) {
  if (!approvals || approvals.length === 0) return [];
  
  const history = [];
  
  approvals.forEach(approval => {
    if (approval.approvedBy && approval.approvedBy.length > 0) {
      approval.approvedBy.forEach(action => {
        history.push({
          stageNumber: approval.stageNumber,
          stageName: approval.stageName,
          userName: action.userName,
          decision: action.decision,
          comment: action.comment,
          timestamp: action.timestamp,
          hasSignature: !!action.signature
        });
      });
    }
  });
  
  return history.sort((a, b) => a.timestamp?.toMillis() - b.timestamp?.toMillis());
}
